import { useEffect, useRef } from 'react';
import type { SpriteKey } from '../../types';
import { FRAME_W, FRAME_H, getDwellerFrames } from './pixelDweller';

// Breathing loop (frame 2 is the blink frame, injected at random).
const BREATH_CYCLE = [0, 1, 0, 3];
const BREATH_MS = 480;

interface Props {
  id: SpriteKey;
  /** Integer pixel scale (4 → 96×128 CSS px). */
  scale?: number;
}

/**
 * The real procedural dweller sprite (same pixels as in the bunker),
 * breathing and blinking. Static when the user prefers reduced motion.
 */
export default function DwellerSprite({ id, scale = 4 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    const frames = getDwellerFrames(id);

    const drawFrame = (i: number): void => {
      ctx.clearRect(0, 0, FRAME_W, FRAME_H);
      ctx.drawImage(frames.idle[i], 0, 0);
    };
    drawFrame(0);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let step = 0;
    let blinkAt = performance.now() + 2000 + Math.random() * 4000;
    const timer = window.setInterval(() => {
      const now = performance.now();
      if (now >= blinkAt) {
        drawFrame(2); // blink
        blinkAt = now + 2500 + Math.random() * 4500;
      } else {
        drawFrame(BREATH_CYCLE[step % BREATH_CYCLE.length]);
      }
      step++;
    }, BREATH_MS);
    return () => window.clearInterval(timer);
  }, [id]);

  return (
    <canvas
      ref={canvasRef}
      width={FRAME_W}
      height={FRAME_H}
      aria-hidden="true"
      style={{
        width: FRAME_W * scale,
        height: FRAME_H * scale,
        imageRendering: 'pixelated',
        display: 'block',
      }}
    />
  );
}
